import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { ARTICULATOR_OPTIONS, NUM_LIMITS, DEFAULT_JIG_SETTINGS, normalizeJigSettings } from '../utils/jigModelSettings';

// ⚙️ 지그·3D 모델 설정 (대표 2026-11-02 "웹 지그 3D 에 설정을" · "에이전트 조정값 칸 없애고 전부 웹으로")
//   저장 = labSettings/{labId}.jigSettings (기존 문서·규칙: 같은 회사 계정 읽기·쓰기, 문서 처음 만들기는 기공소 대표 계정)
//   덴클 프로그램이 10분마다 읽어 그대로 쓴다. 웹에 저장한 적이 없으면 각 PC 의 예전 값을 계속 쓴다.
//   모델 칸(교합기·자동 생성 때 모델)은 3D 모델이 열린 기공소만 보인다.
export default function JigModelSettings({ labId, modelOpen }) {
    const [val, setVal] = useState(DEFAULT_JIG_SETTINGS);
    const [draft, setDraft] = useState({ cementGap: '0.06', holeClearance: '0.3' });   // 숫자 칸 입력 중 값
    const [saved, setSaved] = useState(false);     // 웹에 저장된 값이 있는가
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (!labId) return undefined;
        let alive = true;
        getDoc(doc(db, 'labSettings', labId))
            .then((s) => {
                if (!alive) return;
                const raw = s.exists() ? s.data().jigSettings : null;
                const v = normalizeJigSettings(raw);
                setVal(v); setSaved(!!raw);
                setDraft({ cementGap: String(v.cementGap), holeClearance: String(v.holeClearance) });
                setLoaded(true);
            })
            .catch((e) => { console.error('[지그 설정] 불러오기 실패:', e?.message || e); if (alive) { setLoaded(true); setMsg('설정을 불러오지 못했습니다. 새로고침해 주세요.'); } });
        return () => { alive = false; };
    }, [labId]);

    const save = async (patch) => {
        const prev = val;
        const next = normalizeJigSettings({ ...val, ...patch });
        setVal(next); setSaving(true); setMsg('');
        try {
            await setDoc(doc(db, 'labSettings', labId), {
                jigSettings: { ...next, updatedAt: serverTimestamp(), updatedBy: auth.currentUser?.uid || null },
            }, { merge: true });
            setSaved(true);
            setMsg('저장했습니다. 덴클 프로그램에는 10분 안에 반영됩니다.');
        } catch (e) {
            console.error('[지그 설정] 저장 실패:', e?.code || '', e?.message || e);
            setVal(prev);
            setMsg(e?.code === 'permission-denied'
                ? '저장 권한이 없습니다. 기공소 대표 계정으로 한 번 저장해 주세요.'
                : '저장하지 못했습니다. 잠시 뒤 다시 시도해 주세요.');
        } finally {
            setSaving(false);
        }
    };

    const saveNum = (k) => {
        const [lo, hi] = NUM_LIMITS[k];
        const n = Number(draft[k]);
        if (draft[k] === '' || !Number.isFinite(n) || n < lo || n > hi) {
            setMsg(`${k === 'cementGap' ? '시멘트 간격' : '홀 여유'}은 ${lo}~${hi}mm 사이로 넣어 주세요.`);
            setDraft((d) => ({ ...d, [k]: String(val[k]) }));
            return;
        }
        if (n !== val[k]) save({ [k]: n });
    };

    const off = !loaded || saving;
    return (
        <div style={styles.card}>
            <div style={styles.head}>
                <span style={styles.title}>{'지그·3D 모델 설정'}</span>
                <span style={styles.sub}>{saved ? '기공소 전체에 적용 · 덴클 프로그램이 이 값대로 만듭니다' : '아직 웹에 저장한 설정이 없어 각 PC의 예전 값을 쓰고 있습니다 · 한 번 저장하면 모든 PC가 이 값을 따릅니다'}</span>
            </div>
            <div style={styles.row}>
                <label style={styles.field} title="지그 안쪽과 어버트먼트 벽 사이 간격. 기본 0.06. 꺾임선(마진) 근처 정지면은 0.02로 고정됩니다.">
                    <span style={styles.name}>{'시멘트 간격'}</span>
                    <input style={styles.num} type="number" step="0.01" min={NUM_LIMITS.cementGap[0]} max={NUM_LIMITS.cementGap[1]} disabled={off}
                        value={draft.cementGap} onChange={(e) => setDraft((d) => ({ ...d, cementGap: e.target.value }))} onBlur={() => saveNum('cementGap')} />
                    <em style={styles.unit}>{'mm'}</em>
                </label>
                <label style={styles.field} title="나사 홀 여유 — 반지름에 더하는 값. 기본 0.3 = 지름 +0.6 (나사 2.2 → 홀 2.8).">
                    <span style={styles.name}>{'홀 여유'}</span>
                    <input style={styles.num} type="number" step="0.05" min={NUM_LIMITS.holeClearance[0]} max={NUM_LIMITS.holeClearance[1]} disabled={off}
                        value={draft.holeClearance} onChange={(e) => setDraft((d) => ({ ...d, holeClearance: e.target.value }))} onBlur={() => saveNum('holeClearance')} />
                    <em style={styles.unit}>{'mm'}</em>
                </label>
                {modelOpen && (
                    <label style={styles.field} title="3D 모델에 붙일 교합기 종류">
                        <span style={styles.name}>{'교합기'}</span>
                        <select style={styles.select} value={val.articulator} disabled={off} onChange={(e) => save({ articulator: e.target.value })}>
                            {ARTICULATOR_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                        </select>
                    </label>
                )}
            </div>
            <div style={styles.row2}>
                <label style={styles.field} title="켠 뒤 새로 들어오는 커스텀 케이스를 마지막 수정 10분 뒤 자동으로 보냅니다. 켜기 전부터 있던 후보·실패 건은 보내지 않습니다.">
                    <input type="checkbox" checked={val.autoOn} disabled={off} onChange={(e) => save({ autoOn: e.target.checked })} />
                    <span style={styles.name}>{'자동 생성'}</span>
                </label>
                {modelOpen && (
                    <label style={styles.field}>
                        <input type="checkbox" checked={val.autoModel} disabled={off} onChange={(e) => save({ autoModel: e.target.checked })} />
                        <span style={styles.name}>{'자동 생성 때 3D 모델도'}</span>
                    </label>
                )}
                <span style={styles.hint}>{val.autoOn ? `켬 — 새 케이스를 ${modelOpen && val.autoModel ? '지그 + 3D 모델' : '지그만'} 자동으로 보냅니다` : '끔 — 덴클 프로그램에서 고른 것만 보냅니다'}</span>
            </div>
            {msg && <div style={styles.msg}>{msg}</div>}
        </div>
    );
}

const styles = {
    card: { border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', margin: '8px 0 12px', backgroundColor: '#f8fafc' },
    head: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
    title: { fontSize: 13, fontWeight: 700, color: '#334155' },
    sub: { fontSize: 11.5, color: '#64748b' },
    row: { display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' },
    row2: { display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0' },
    field: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#334155' },
    name: { fontWeight: 600 },
    num: { width: 70, fontSize: 12.5, padding: '3px 6px', borderRadius: 6, border: '1px solid #cbd5e1' },
    unit: { fontSize: 11, color: '#94a3b8', fontStyle: 'normal' },
    select: { fontSize: 12.5, padding: '3px 6px', borderRadius: 6, border: '1px solid #cbd5e1' },
    hint: { fontSize: 11.5, color: '#64748b' },
    msg: { fontSize: 11.5, color: '#475569', marginTop: 6 },
};
